import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import FanAvatar from "./FanAvatar";
import {
  BENEFIT_FILL as FILL,
  BENEFIT_GREEN as GREEN,
  type IgConnectBenefit,
  type IgConnectView,
  connectView,
  doneView,
  expiredView,
  igConnectStage,
  waitingView,
} from "./IgConnectSteps";
import { useAndroidBack } from "../hooks/useAndroidBack";
import { type IgDmRail, TALENT_DM_RAIL, useInstagramDmVerify } from "../hooks/useInstagramDmVerify";
import { onInstagramBrowserClosed } from "../lib/instagramLogin";
import { t } from "../i18n";

// Talent "Connect Instagram" — the fan screens (components/IgConnectSteps),
// talent words. One flow, three doors (founder, connect-socials v3/v4, 10/30):
//   - sign-up step 2 of 3 (pages/onboarding/steps/StepConnectInstagram),
//   - Edit profile's Verify, and tapping its Instagram box (ProfilePage),
//   - the "Verify your Instagram" sheet's Continue (components/ReviewSheet),
// the last two only while both Instagram switches are on for the talent.
//
//   connect ─Connect Instagram→ Meta's Instagram sign-in (Business / Creator)
//     │                           ├ vouched → the page comes back with entry "done"
//     │                           ╰ "personal account" → back with entry "personal"
//     │ Back / Skip for now → onClose (Back: onBack)
//   personal ─(the message starts by itself)→ waiting ─the DM arrives→ done ─Continue→ onClose
//                                              │ Cancel → connect (Connect now starts the message)
//                                              ├ another talent holds that Instagram → onTaken
//                                              ╰ 30 minutes → expired ─Try again→ waiting
//                                                              ╰ Back → connect, Skip for now → onClose
//
// Without `startLogin` (opened after a "personal" answer) Connect goes straight
// to the message.

/** The founder's three lines (v3): the verified rosette, the follow "+", the shield. */
export const talentIgBenefits = (): IgConnectBenefit[] => [
  {
    text: t("talentIgConnect.benefitBadge"),
    icon: (
      <>
        <path
          d="M12 2.2 14.1 3.6 16.6 3.5 17.7 5.7 19.9 6.8 19.8 9.3 21.2 11.4 19.8 13.5 19.9 16 17.7 17.1 16.6 19.3 14.1 19.2 12 20.6 9.9 19.2 7.4 19.3 6.3 17.1 4.1 16 4.2 13.5 2.8 11.4 4.2 9.3 4.1 6.8 6.3 5.7 7.4 3.5 9.9 3.6Z"
          fill={FILL}
          stroke={GREEN}
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path d="m8.6 11.4 2.3 2.3 4.6-4.6" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
  {
    text: t("talentIgConnect.benefitFollowers"),
    icon: (
      <>
        <circle cx="10" cy="8" r="4" fill={FILL} stroke={GREEN} strokeWidth="1.8" />
        <path d="M2.8 20.5c.6-3.9 3.5-6.3 7.2-6.3 1.4 0 2.6.3 3.7.9" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M18.5 14v6.4M15.3 17.2h6.4" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" />
      </>
    ),
  },
  {
    text: t("fanConnect.benefitSafe"),
    icon: (
      <>
        <path d="M12 3.2 19 6v5.6c0 4.3-3 7.6-7 9.2-4-1.6-7-4.9-7-9.2V6Z" fill={FILL} stroke={GREEN} strokeWidth="1.8" strokeLinejoin="round" />
        <path d="m8.8 12.1 2.2 2.2 4.3-4.4" stroke={GREEN} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </>
    ),
  },
];

type Step = "connect" | "waiting" | "done" | "expired";
export type TalentIgEntry = "connect" | "personal" | "done";

type Props = {
  /** The talent's name and photo, for the "you" bubble (initials without a photo). */
  name: string;
  avatarUrl: string | null;
  /** The first screen: Connect; the message straight away (Meta's sign-in said
      "personal account"); or "Instagram connected!" (Meta's sign-in vouched). */
  entry?: TalentIgEntry;
  /** entry "done": the handle Instagram vouched for. */
  username?: string;
  /** Opens Meta's Instagram sign-in; resolves false when it couldn't start. */
  startLogin?: () => Promise<boolean>;
  /** The message round trip's endpoints: the signed-in talent's, or a sign-up's. */
  rail?: IgDmRail;
  /** Sign-up's "Step 2 of 3", on the Connect screen only. */
  strip?: React.ReactNode;
  /** One line under Connect: how the last try came back (taken, didn't go through). */
  note?: string;
  /** Drawn over the app's sheets (Edit profile, the Verify sheet); sign-up is a page. */
  over?: boolean;
  /** Instagram vouched for this handle by the message (the screen shows "connected" meanwhile). */
  onVerified: (username: string) => void;
  /** That Instagram already vouches for another talent. */
  onTaken: () => void;
  /** Back on the Connect screen (onClose when absent). */
  onBack?: () => void;
  /** The in-app Instagram sheet closed without coming back (Done): re-check. */
  onSheetClosed?: () => void;
  /** Skip, or Continue after it worked. */
  onClose: () => void;
};

export default function TalentIgConnect({
  name,
  avatarUrl,
  entry = "connect",
  username: vouched = "",
  startLogin,
  rail = TALENT_DM_RAIL,
  strip,
  note,
  over = true,
  onVerified,
  onTaken,
  onBack,
  onSheetClosed,
  onClose,
}: Props) {
  const [step, setStep] = useState<Step>(entry === "done" ? "done" : entry === "personal" ? "waiting" : "connect");
  const [username, setUsername] = useState(vouched);
  // Meta's sign-in said "personal account": from now on Connect starts the message.
  const [dmOnly, setDmOnly] = useState(entry === "personal" || !startLogin);
  const [opening, setOpening] = useState(false);
  const [loginFailed, setLoginFailed] = useState(false);
  const v = useInstagramDmVerify((u) => {
    setUsername(u);
    onVerified(u);
    setStep("done");
  }, rail);

  // Back from Meta's sign-in with "personal account": the message starts by
  // itself, but Instagram opens on the talent's own tap — they just left it.
  const startedRef = useRef(false);
  useEffect(() => {
    if (entry !== "personal" || startedRef.current) return;
    startedRef.current = true;
    void v.start({ open: false });
  }, [entry, v]);

  const onTakenRef = useRef(onTaken);
  onTakenRef.current = onTaken;
  // The round trip drives the screen, as on the fan side.
  useEffect(() => {
    if (v.phase === "waiting") setStep("waiting");
    else if (v.phase === "expired") setStep("expired");
    else if (v.phase === "taken") onTakenRef.current();
    // The message that started by itself couldn't: Connect, with its line.
    else if (v.phase === "failed") setStep((s) => (s === "waiting" ? "connect" : s));
  }, [v.phase]);

  // The phone's Instagram sheet: Done (or the hand-off back) re-enables
  // Connect, and the owner re-checks what came back.
  const sheetClosedRef = useRef(onSheetClosed);
  sheetClosedRef.current = onSheetClosed;
  useEffect(
    () =>
      onInstagramBrowserClosed(() => {
        setOpening(false);
        sheetClosedRef.current?.();
      }),
    []
  );

  const login = async () => {
    if (!startLogin) return;
    setLoginFailed(false);
    setOpening(true);
    const started = await startLogin();
    if (!started) {
      setOpening(false);
      setLoginFailed(true);
    }
  };

  const cancel = () => {
    v.cancel();
    setDmOnly(true);
    setStep("connect");
  };

  // The Android back button does what each step's own back does: Back on
  // Connect, Cancel on the wait, Back on "too long", Continue once connected.
  useAndroidBack(() => {
    if (step === "waiting") cancel();
    else if (step === "expired") setStep("connect");
    else if (step === "connect") (onBack || onClose)();
    else onClose();
  });

  // Connect: Meta's sign-in, or — once Instagram said "personal" — the message,
  // whose own failure line wins over the owner's note.
  const connectAction = dmOnly
    ? v.phase === "failed" || !note
      ? {}
      : { note }
    : { onConnect: () => void login(), busy: opening, note: loginFailed ? t("fanConnect.failed") : note || "" };

  let view: IgConnectView;
  if (step === "connect") {
    view = connectView({
      you: <FanAvatar className="fcf-you" name={name} avatarUrl={avatarUrl} silhouetteSize={40} />,
      benefits: talentIgBenefits(),
      v,
      onBack: onBack || onClose,
      onSkip: onClose,
      ...connectAction,
    });
  } else if (step === "waiting") {
    view = waitingView({ v, onCancel: cancel });
  } else if (step === "done") {
    view = doneView({ username, continueLabel: t("fanConnect.continue"), onContinue: onClose });
  } else {
    view = expiredView({ v, onBack: () => setStep("connect"), onSkip: onClose });
  }

  const stage = igConnectStage(step, view, over, step === "connect" ? strip : undefined);
  return over ? createPortal(stage, document.body) : stage;
}
